'use client';

import { useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarPlus,
  ChevronRight,
  type LucideIcon,
  Package,
  PartyPopper,
  QrCode,
  UserPlus,
  UsersRound,
} from 'lucide-react';

import {
  APP_TABS,
  AppTabBar,
  Avatar,
  DEMO_BRANDS,
  type DemoBrand,
  type DemoProps,
  DemoStage,
  type DemoText,
  EASE_OUT_EXPO,
  FakeQr,
  PhoneFrame,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * White-label proof: the same member app re-skinned for three venues. Accent
 * colour, brand mark, typography and home-screen content all follow the
 * venue, while the product underneath stays identical.
 */


export const BRANDED_APP_BRANDS = [
  DEMO_BRANDS.padel,
  DEMO_BRANDS.studio,
  DEMO_BRANDS.club,
] as const;

const DURATIONS = [2800, 2800, 2800] as const;

const COPY = {
  label: {
    en: 'Animated demo of one member app re-skinned for three venues, a padel club, a pilates studio and a members club, each with its own brand colours and home screen.',
    ar: 'عرض متحرك لتطبيق أعضاء واحد يتبدّل بين ثلاث منشآت، نادي بادل واستوديو بيلاتس ونادي أعضاء، ولكل منها ألوانها وشاشتها الرئيسية.',
  },
  thisWeek: { en: 'This week', ar: 'هذا الأسبوع' },
  ...APP_TABS,
} as const;

const SERIF = 'Georgia, "Times New Roman", serif';

type HeroKind = 'match' | 'class' | 'membership';

interface Skin {
  brand: DemoBrand;
  hero: HeroKind;
  radius: number;
  font?: string;
  greeting: DemoText;
  heroLabel: DemoText;
  heroTitle: DemoText;
  heroMeta: DemoText;
  heroBadge: DemoText;
  actions: readonly [DemoText, DemoText, DemoText];
  actionIcons: readonly [LucideIcon, LucideIcon, LucideIcon];
  upcoming: readonly { title: DemoText; when: DemoText }[];
}

const SKINS: readonly Skin[] = [
  {
    brand: DEMO_BRANDS.padel,
    hero: 'match',
    radius: 20,
    greeting: { en: 'Good evening, Sara', ar: 'مساء الخير، سارة' },
    heroLabel: { en: 'Next match', ar: 'مباراتك القادمة' },
    heroTitle: { en: 'Court 3 · Thu 8:00 PM', ar: 'الملعب 3 · الخميس 8:00 م' },
    heroMeta: { en: 'Doubles · 90 minutes', ar: 'زوجي · 90 دقيقة' },
    heroBadge: { en: 'Pass', ar: 'التذكرة' },
    actions: [
      { en: 'Book a court', ar: 'احجز ملعبًا' },
      { en: 'Coaching', ar: 'التدريب' },
      { en: 'Packages', ar: 'الباقات' },
    ],
    actionIcons: [CalendarPlus, UsersRound, Package],
    upcoming: [
      {
        title: { en: 'Coaching with Karim', ar: 'تدريب مع كريم' },
        when: { en: 'Sat 6:00 PM', ar: 'السبت 6:00 م' },
      },
      {
        title: { en: 'Americano night', ar: 'أمسية أمريكانو' },
        when: { en: 'Sun 7:30 PM', ar: 'الأحد 7:30 م' },
      },
      {
        title: { en: 'Ladies clinic', ar: 'حصة جماعية للسيدات' },
        when: { en: 'Tue 6:00 PM', ar: 'الثلاثاء 6:00 م' },
      },
    ],
  },
  {
    brand: DEMO_BRANDS.studio,
    hero: 'class',
    radius: 28,
    font: SERIF,
    greeting: { en: 'Good morning, Sara', ar: 'صباح الخير، سارة' },
    heroLabel: { en: 'Next class', ar: 'حصتك القادمة' },
    heroTitle: { en: 'Reformer Flow', ar: 'ريفورمر فلو' },
    heroMeta: { en: '7:00 AM · with Noor', ar: '7:00 ص · مع نور' },
    heroBadge: { en: '7 credits left', ar: 'متبقٍ 7 حصص' },
    actions: [
      { en: 'Book a class', ar: 'احجز حصة' },
      { en: 'Packages', ar: 'الباقات' },
      { en: 'Instructors', ar: 'المدربون' },
    ],
    actionIcons: [CalendarPlus, Package, UsersRound],
    upcoming: [
      {
        title: { en: 'Mat Pilates', ar: 'بيلاتس مات' },
        when: { en: 'Tue 8:30 AM', ar: 'الثلاثاء 8:30 ص' },
      },
      {
        title: { en: 'Barre', ar: 'بار' },
        when: { en: 'Thu 6:00 PM', ar: 'الخميس 6:00 م' },
      },
      {
        title: { en: 'Reformer Sculpt', ar: 'ريفورمر سكَلبت' },
        when: { en: 'Sat 10:00 AM', ar: 'السبت 10:00 ص' },
      },
    ],
  },
  {
    brand: DEMO_BRANDS.club,
    hero: 'membership',
    radius: 14,
    greeting: { en: 'Welcome back, Sara', ar: 'أهلًا بعودتك، سارة' },
    heroLabel: { en: 'Family membership', ar: 'عضوية عائلية' },
    heroTitle: { en: 'Sara Al-Masri', ar: 'سارة المصري' },
    heroMeta: { en: 'Member since 2023', ar: 'عضو منذ 2023' },
    heroBadge: { en: 'Active', ar: 'فعّالة' },
    actions: [
      { en: 'Book a court', ar: 'احجز ملعبًا' },
      { en: 'Events', ar: 'الفعاليات' },
      { en: 'Guest pass', ar: 'دعوة ضيف' },
    ],
    actionIcons: [CalendarPlus, PartyPopper, UserPlus],
    upcoming: [
      {
        title: { en: 'Tennis social', ar: 'أمسية تنس اجتماعية' },
        when: { en: 'Fri 7:00 PM', ar: 'الجمعة 7:00 م' },
      },
      {
        title: { en: 'Kids tennis camp', ar: 'مخيم التنس للأطفال' },
        when: { en: 'Sat 9:00 AM', ar: 'السبت 9:00 ص' },
      },
      {
        title: { en: 'Family brunch', ar: 'فطور العائلات' },
        when: { en: 'Sun 11:00 AM', ar: 'الأحد 11:00 ص' },
      },
    ],
  },
];

const CROSSFADE = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.55, ease: EASE_OUT_EXPO },
} as const;

export function BrandedAppDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS, { staticStep: 0 });
  const copy = localize(COPY, locale);
  const skin = SKINS[timeline.step % SKINS.length]!;
  const brand = skin.brand;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <div className="flex flex-col items-center gap-4">
        <div className="relative flex h-8 items-center gap-2 overflow-hidden rounded-full border border-[#dcd7cf] bg-white px-3.5 shadow-[0_8px_24px_-12px_rgba(32,32,32,0.3)]">
          <motion.span
            className="h-2 w-2 shrink-0 rounded-full"
            initial={false}
            animate={{ backgroundColor: brand.accent }}
            transition={{ duration: 0.5 }}
          />
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={brand.mark}
              className="whitespace-nowrap text-[12px] font-medium text-[#202020]"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {brand.name[locale]}
            </motion.span>
          </AnimatePresence>
        </div>

        <PhoneFrame>
          <motion.div
            className="absolute inset-0"
            initial={false}
            animate={{
              background: `linear-gradient(180deg, ${brand.accentSoft} 0%, #fbfaf8 42%)`,
            }}
            transition={{ duration: 0.6 }}
          />

          <div className="absolute inset-x-0 top-[44px] z-10 flex items-center justify-between px-5 pb-3 pt-2">
            <div className="flex items-center gap-2.5">
              <motion.span
                className="inline-flex h-7 w-7 items-center justify-center text-[13px] font-bold text-white"
                initial={false}
                animate={{
                  backgroundColor: brand.accent,
                  borderRadius: skin.hero === 'class' ? 14 : 9,
                }}
                transition={{ duration: 0.5 }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={brand.mark}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.2 }}
                  >
                    {brand.mark}
                  </motion.span>
                </AnimatePresence>
              </motion.span>
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={brand.mark}
                  className="text-[13px] font-semibold text-[#202020]"
                  style={{ fontFamily: skin.font }}
                  initial={{ opacity: 0, x: 6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.25 }}
                >
                  {brand.name[locale]}
                </motion.span>
              </AnimatePresence>
            </div>
            <Avatar initials="SA" tone={1} size={28} />
          </div>

          <AnimatePresence initial={false}>
            <motion.div
              key={brand.mark}
              className="absolute inset-x-0 top-[88px] bottom-[64px] px-4"
              {...CROSSFADE}
            >
              <SkinScreen skin={skin} locale={locale} thisWeek={copy.thisWeek} />
            </motion.div>
          </AnimatePresence>

          <AnimatePresence initial={false}>
            <motion.div
              key={brand.mark}
              className="pointer-events-none absolute inset-0 z-[5]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
            >
              <AppTabBar
                active={0}
                accent={brand.accent}
                labels={[copy.home, copy.book, copy.bookings, copy.profile]}
              />
            </motion.div>
          </AnimatePresence>
        </PhoneFrame>
      </div>
    </DemoStage>
  );
}

function SkinScreen({
  skin,
  locale,
  thisWeek,
}: {
  skin: Skin;
  locale: DemoProps['locale'];
  thisWeek: string;
}) {
  const brand = skin.brand;

  return (
    <div className="flex h-full flex-col">
      <p
        className="px-1 text-[20px] font-bold leading-tight text-[#202020]"
        style={{ fontFamily: skin.font }}
      >
        {skin.greeting[locale]}
      </p>

      <HeroCard skin={skin} locale={locale} />

      <div className="mt-3.5 grid grid-cols-3 gap-2">
        {skin.actions.map((action, index) => {
          const Icon = skin.actionIcons[index]!;

          return (
            <div
              key={action.en}
              className="flex h-[62px] flex-col items-center justify-center gap-1.5 border border-[#ebe7e1] bg-white"
              style={{ borderRadius: Math.max(12, skin.radius - 8) }}
            >
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full"
                style={{ background: brand.accentSoft, color: brand.accent }}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
              </span>
              <span className="whitespace-nowrap text-[9.5px] font-medium text-[#202020]">
                {action[locale]}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-4 px-1 font-roboto-mono text-[9.5px] uppercase tracking-[0.12em] text-[#8a857c]">
        {thisWeek}
      </p>

      <div className="mt-2 space-y-1.5">
        {skin.upcoming.map((item) => (
          <div
            key={item.title.en}
            className="flex h-[42px] items-center gap-2.5 border border-[#ebe7e1] bg-white px-3"
            style={{ borderRadius: Math.max(12, skin.radius - 8) }}
          >
            <span className="h-6 w-1 rounded-full" style={{ background: brand.accent }} />
            <span className="flex-1 truncate text-[11.5px] font-semibold text-[#202020]">
              {item.title[locale]}
            </span>
            <span className="whitespace-nowrap text-[10px] text-[#8a857c]">
              {item.when[locale]}
            </span>
            <ChevronRight className="h-3 w-3 text-[#b5b0a6] rtl:rotate-180" />
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroCard({
  skin,
  locale,
}: {
  skin: Skin;
  locale: DemoProps['locale'];
}) {
  const brand = skin.brand;

  if (skin.hero === 'membership') {
    return (
      <div
        className="relative mt-3 h-[124px] overflow-hidden p-4 text-white"
        style={{
          borderRadius: skin.radius,
          background: `linear-gradient(135deg, ${brand.accent} 0%, #1c2e24 100%)`,
        }}
      >
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 11px)',
          }}
        />
        <div className="relative flex h-full items-stretch justify-between gap-3">
          <div className="flex flex-col justify-between">
            <div>
              <p className="font-roboto-mono text-[8.5px] uppercase tracking-[0.16em] text-white/70">
                {skin.heroLabel[locale]}
              </p>
              <p className="mt-1.5 text-[15px] font-semibold">{skin.heroTitle[locale]}</p>
              <p className="text-[10px] text-white/70">{skin.heroMeta[locale]}</p>
            </div>
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-2 py-0.5 text-[9.5px] font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7ee2a8]" />
              {skin.heroBadge[locale]}
            </span>
          </div>
          <span className="flex h-[70px] w-[70px] shrink-0 items-center justify-center self-end rounded-[10px] bg-white">
            <FakeQr size={58} seed={31} color="#1c2e24" />
          </span>
        </div>
      </div>
    );
  }

  const isClass = skin.hero === 'class';

  return (
    <div
      className="relative mt-3 h-[124px] overflow-hidden p-4 text-white"
      style={{ borderRadius: skin.radius, background: brand.accent }}
    >
      {isClass ? (
        <span className="absolute -end-10 -top-12 h-40 w-40 rounded-full border-[18px] border-white/10" />
      ) : (
        <svg
          className="absolute -end-4 top-3 h-[104px] w-[120px] text-white/12"
          viewBox="0 0 120 104"
          fill="none"
          aria-hidden="true"
        >
          <rect x="1" y="1" width="118" height="102" rx="4" stroke="currentColor" strokeWidth="2" />
          <line x1="60" y1="1" x2="60" y2="103" stroke="currentColor" strokeWidth="2" />
          <line x1="1" y1="30" x2="119" y2="30" stroke="currentColor" strokeWidth="2" />
          <line x1="1" y1="74" x2="119" y2="74" stroke="currentColor" strokeWidth="2" />
        </svg>
      )}
      <div className="relative flex h-full flex-col justify-between">
        <div>
          <p className="font-roboto-mono text-[8.5px] uppercase tracking-[0.16em] text-white/70">
            {skin.heroLabel[locale]}
          </p>
          <p
            className="mt-1.5 text-[16px] font-semibold leading-snug"
            style={{ fontFamily: skin.font }}
          >
            {skin.heroTitle[locale]}
          </p>
          <p className="text-[10.5px] text-white/75">{skin.heroMeta[locale]}</p>
        </div>
        <div className="flex items-center justify-between">
          {isClass ? (
            <span className="h-1 w-24 overflow-hidden rounded-full bg-white/20">
              <span className="block h-full w-[70%] rounded-full bg-white/80" />
            </span>
          ) : (
            <span className="flex -space-x-1.5 rtl:space-x-reverse">
              {['SA', 'MK', 'RH', 'OT'].map((initials, index) => (
                <Avatar
                  key={initials}
                  initials={initials}
                  tone={index}
                  size={22}
                  className="ring-[1.5px] ring-white/40"
                />
              ))}
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[9.5px] font-medium">
            {!isClass && <QrCode className="h-3 w-3" />}
            {skin.heroBadge[locale]}
          </span>
        </div>
      </div>
    </div>
  );
}
